import { useCallback, useEffect, useMemo } from "react";
import _ from "lodash";

import { AddIcon, LockIcon, MessageOutlinedIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import PusherManager from "utils/pusher-manager";

import Badge from "components/Badge";
import Button from "components/Button";
import Stack from "components/Stack";
import Text from "components/Text";

import { useSignedUser } from "entities/Auth/sdk";
import { useDealSidepanel } from "entities/Deal/components/Sidepanel/useDealSidepanel";
import { TASKS_TAB_SUBTABS } from "entities/Deal/components/Sidepanel/utils";
import { useDealPermission } from "entities/Deal/sdk";
import TaskListSkeleton from "entities/Task/components/TaskListSkeleton";
import { ITaskChannel, useTaskChannels } from "entities/Task/sdk";

interface ITaskList {
  dealId: number;
  taskChannels?: Array<ITaskChannel>;
}

const TaskList = ({ dealId, taskChannels }: ITaskList) => {
  const { meta, setMeta } = useDealSidepanel();
  const { data: user } = useSignedUser();

  const { mutate: updateTaskChannels, loading } = useTaskChannels({ dealId });
  const { data: userPermission } = useDealPermission({ dealId });

  const openChannelDetail = useCallback(
    (taskChannel: ITaskChannel) => {
      if (!_.isNil(meta)) {
        setMeta({
          ...meta,
          subTab: TASKS_TAB_SUBTABS.DETAILS,
          taskChannel: taskChannel,
        });
      }
    },
    [meta, setMeta]
  );

  const openNewTaskChannel = useCallback(() => {
    if (!_.isNil(meta)) {
      setMeta({
        ...meta,
        subTab: TASKS_TAB_SUBTABS.ADD_TASK,
        taskChannel: undefined,
      });
    }
  }, [meta, setMeta]);

  const eventHandler = useCallback(
    (receivedMsg: any) => {
      if (receivedMsg.deal_id === dealId) {
        updateTaskChannels();
      }
    },
    [updateTaskChannels, dealId]
  );

  useEffect(() => {
    if (user) {
      return PusherManager.bindToTaskChat({ userId: user.id, eventHandler });
    }
  }, [eventHandler, user]);

  const openTasks = useMemo(
    () =>
      taskChannels?.filter((taskChannel) => !taskChannel.task.completed) || [],
    [taskChannels]
  );
  const closedTasks = useMemo(
    () =>
      taskChannels?.filter((taskChannel) => taskChannel.task.completed) || [],
    [taskChannels]
  );

  const renderTaskGroup = useCallback(
    ({ tasks, label }: { tasks: Array<ITaskChannel>; label: string }) =>
      !_.isEmpty(tasks) &&
      !loading && (
        <Stack marginBottom={3} data-testid="tasks-list">
          <Text variant="text3" sx={{ color: colors.gray60, marginBottom: 1 }}>
            {tasks.length} {label} task{tasks.length === 1 ? "" : "s"} listed
          </Text>

          {tasks.map((taskChannel) => (
            <Stack
              key={taskChannel.id}
              direction="row"
              justifyContent="space-between"
              sx={{
                cursor: "pointer",
                padding: theme.spacing(1, 2),
                borderRadius: theme.spacing(0.5),
                "&:hover": {
                  backgroundColor: colors.blue5,
                },
              }}
              onClick={() => openChannelDetail(taskChannel)}
              data-testid="open-task"
            >
              <Stack direction="row" spacing={1}>
                {!["", null].includes(taskChannel.task.visible_to_team) && (
                  <LockIcon />
                )}
                <Text data-testid="task-title">{taskChannel.task.title}</Text>
              </Stack>
              {taskChannel.message_count !== 0 && (
                <Badge
                  badgeContent={taskChannel.unread_count}
                  color="primary"
                  data-testid="task-chat-icon"
                >
                  <MessageOutlinedIcon
                    height="20px"
                    sx={{ color: colors.gray60 }}
                  />
                </Badge>
              )}
            </Stack>
          ))}
        </Stack>
      ),
    [loading, openChannelDetail]
  );

  const canCreateTask = userPermission?.is_full_access;

  return (
    <>
      <Stack alignItems="flex-end" sx={{ marginBottom: theme.spacing(4) }}>
        {canCreateTask && (
          <Button startIcon={<AddIcon />} onClick={openNewTaskChannel}>
            Add New Task
          </Button>
        )}
      </Stack>

      {_.isEmpty(taskChannels) && !loading && (
        <Stack spacing={3} margin={5} data-testid="empty-task-list">
          <Text variant="text2" fontWeight={700}>
            No tasks found...
          </Text>
          <Text variant="text2">We couldn't find any tasks for this deal.</Text>
        </Stack>
      )}

      {loading && <TaskListSkeleton />}

      {renderTaskGroup({ tasks: openTasks, label: "open" })}

      {renderTaskGroup({ tasks: closedTasks, label: "closed" })}
    </>
  );
};

export default TaskList;
